"use client"

import {
  createContext,
  useContext,
  useId,
  useRef,
  useState,
} from "react"
import {
  DndContext,
  DragOverlay,
  MouseSensor,
  PointerSensor,
  TouchSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core"
import { addMinutes, differenceInMinutes } from "date-fns"

import { EventItem } from "./event-item"
import { TimeSlotIntervalMinutes } from "./constants"

const roundTimeToInterval = (timeValue) => {
  const interval = TimeSlotIntervalMinutes
  const totalMinutes = Math.round((timeValue * 60) / interval) * interval
  const hours = Math.floor(totalMinutes / 60)
  const minutes = totalMinutes % 60
  return { hours, minutes }
}

/**
 * @typedef {import("./types").CalendarEvent} CalendarEvent
 */

const CalendarDndContext = createContext({
  activeEvent: null,
  activeId: null,
  activeView: null,
  currentTime: null,
  eventHeight: null,
  isMultiDay: false,
  multiDayWidth: null,
  dragHandlePosition: null,
  timezone: null,
  timeFormat: "24h",
})

export const useCalendarDnd = () => useContext(CalendarDndContext)

export function CalendarDndProvider({
  children,
  onEventUpdate,
  timezone,
  timeFormat = "24h",
}) {
  const [activeEvent, setActiveEvent] = useState(null)
  const [activeId, setActiveId] = useState(null)
  const [activeView, setActiveView] = useState(null)
  const [currentTime, setCurrentTime] = useState(null)
  const [eventHeight, setEventHeight] = useState(null)
  const [isMultiDay, setIsMultiDay] = useState(false)
  const [multiDayWidth, setMultiDayWidth] = useState(null)
  const [dragHandlePosition, setDragHandlePosition] = useState(null)

  const eventDimensions = useRef({ height: 0 })

  const sensors = useSensors(
    useSensor(MouseSensor, {
      activationConstraint: { distance: 5 },
    }),
    useSensor(TouchSensor, {
      activationConstraint: { delay: 250, tolerance: 5 },
    }),
    useSensor(PointerSensor, {
      activationConstraint: { distance: 5 },
    })
  )

  const dndContextId = useId()

  const handleDragStart = (event) => {
    const { active } = event
    const data = active?.data?.current

    if (!data || !data.event || !data.view) {
      console.error("Missing data in drag start event", event)
      return
    }

    const {
      event: calendarEvent,
      view,
      height,
      isMultiDay: eventIsMultiDay,
      multiDayWidth: eventMultiDayWidth,
      dragHandlePosition: eventDragHandlePosition,
    } = data

    setActiveEvent(calendarEvent)
    setActiveId(active.id)
    setActiveView(view)
    setCurrentTime(new Date(calendarEvent.start))
    setIsMultiDay(Boolean(eventIsMultiDay))
    setMultiDayWidth(eventMultiDayWidth ?? null)
    setDragHandlePosition(eventDragHandlePosition ?? null)

    if (height) {
      eventDimensions.current.height = height
      setEventHeight(height)
    }
  }

  const handleDragOver = (event) => {
    const { over } = event

    if (!over || !activeEvent || !over.data?.current) return

    const { date, time } = over.data.current

    if (!date) return

    if (time !== undefined && activeView !== "month") {
      const newTime = new Date(date)
      const { hours, minutes } = roundTimeToInterval(time)
      newTime.setHours(hours, minutes, 0, 0)

      if (
        !currentTime ||
        newTime.getHours() !== currentTime.getHours() ||
        newTime.getMinutes() !== currentTime.getMinutes() ||
        newTime.getDate() !== currentTime.getDate() ||
        newTime.getMonth() !== currentTime.getMonth() ||
        newTime.getFullYear() !== currentTime.getFullYear()
      ) {
        setCurrentTime(newTime)
      }
    } else if (activeView === "month") {
      const newTime = new Date(date)
      if (currentTime) {
        newTime.setHours(
          currentTime.getHours(),
          currentTime.getMinutes(),
          currentTime.getSeconds(),
          currentTime.getMilliseconds()
        )
      }

      if (
        !currentTime ||
        newTime.getDate() !== currentTime.getDate() ||
        newTime.getMonth() !== currentTime.getMonth() ||
        newTime.getFullYear() !== currentTime.getFullYear()
      ) {
        setCurrentTime(newTime)
      }
    }
  }

  const resetState = () => {
    setActiveEvent(null)
    setActiveId(null)
    setActiveView(null)
    setCurrentTime(null)
    setEventHeight(null)
    setIsMultiDay(false)
    setMultiDayWidth(null)
    setDragHandlePosition(null)
  }

  const handleDragEnd = (event) => {
    const { active, over } = event

    if (!over || !activeEvent || !currentTime) {
      resetState()
      return
    }

    try {
      const activeData = active?.data?.current || {}
      const overData = over?.data?.current || {}

      const calendarEvent = activeData.event
      const date = overData.date
      const time = overData.time
      const resourceId = overData.resourceId
      const resourceType = overData.resourceType

      if (!calendarEvent || !date) {
        throw new Error("Missing required event data")
      }

      const newStart = new Date(date)

      if (time !== undefined) {
        const { hours: roundedHours, minutes } = roundTimeToInterval(time)
        newStart.setHours(roundedHours, minutes, 0, 0)
      } else {
        newStart.setHours(
          currentTime.getHours(),
          currentTime.getMinutes(),
          currentTime.getSeconds(),
          currentTime.getMilliseconds()
        )
      }

      const originalStart = new Date(calendarEvent.start)
      const originalEnd = new Date(calendarEvent.end)
      const durationMinutes = differenceInMinutes(originalEnd, originalStart)
      const newEnd = addMinutes(newStart, durationMinutes)

      let updatedEvent = {
        ...calendarEvent,
        start: newStart,
        end: newEnd,
      }

      if (resourceId && resourceType === "chairs") {
        updatedEvent = {
          ...updatedEvent,
          chairId: resourceId,
        }
      } else if (resourceId && resourceType === "doctors") {
        updatedEvent = {
          ...updatedEvent,
          doctorId: resourceId,
        }
      }

      const hasStartTimeChanged =
        originalStart.getFullYear() !== newStart.getFullYear() ||
        originalStart.getMonth() !== newStart.getMonth() ||
        originalStart.getDate() !== newStart.getDate() ||
        originalStart.getHours() !== newStart.getHours() ||
        originalStart.getMinutes() !== newStart.getMinutes()

      const hasResourceChanged =
        (resourceType === "chairs" && resourceId !== calendarEvent?.chairId) ||
        (resourceType === "doctors" && resourceId !== calendarEvent?.doctorId)

      if (hasStartTimeChanged || hasResourceChanged) {
        onEventUpdate(updatedEvent)
      }
    } catch (error) {
      console.error("Error in drag end handler:", error)
    } finally {
      resetState()
    }
  }

  return (
    <DndContext
      id={dndContextId}
      sensors={sensors}
      onDragStart={handleDragStart}
      onDragOver={handleDragOver}
      onDragEnd={handleDragEnd}
    >
      <CalendarDndContext.Provider
        value={{
          activeEvent,
          activeId,
          activeView,
          currentTime,
          eventHeight,
          isMultiDay,
          multiDayWidth,
          dragHandlePosition,
          timezone,
          timeFormat,
        }}
      >
        {children}

        <DragOverlay adjustScale={false} dropAnimation={null}>
          {activeEvent && activeView && (
            <div
              style={{
                height: eventHeight ? `${eventHeight}px` : "auto",
                width:
                  isMultiDay && multiDayWidth ? `${multiDayWidth}%` : "100%",
              }}
            >
              <EventItem
                event={activeEvent}
                view={activeView}
                isDragging={true}
                showTime={activeView !== "month"}
                currentTime={currentTime || undefined}
                isFirstDay={dragHandlePosition?.data?.isFirstDay !== false}
                isLastDay={dragHandlePosition?.data?.isLastDay !== false}
                timezone={timezone}
                timeFormat={timeFormat}
              />
            </div>
          )}
        </DragOverlay>
      </CalendarDndContext.Provider>
    </DndContext>
  )
}
